/**
 * realestate.com.au asset extraction.
 *
 * The listing page embeds its whole gallery as JSON: `MediaImage` and
 * `MediaFloorplan` objects whose `templatedUrl` carries a `{size}`
 * placeholder (i2.au.reastatic.net/{size}/<hash>/image.jpg). We read those
 * first and fill in a usable size; the `<img>` tags on the page are only
 * 360x270 thumbnails of the first few photos. Verified 2026-09-26 on a
 * stored Albert Park page: 18 photos + 1 floorplan in the JSON, 4 in `<img>`.
 *
 * Older layouts fall back to `<img>` tags on the REA CDN (i.au, i2.au, ...)
 * with cache-busting `?fit=...&...` query strings stripped so re-scrapes
 * hit the same canonical URL.
 *
 * Floorplans: REA marks them with `data-testid="floorplan-..."` on
 * gallery thumbnails and uses alt text containing "floor plan" or
 * "floorplan" on the image itself.
 *
 * PDFs: rare on REA detail pages — usually statement of information
 * (StatementOfInformation) or vendor's brochure. Falls through to
 * the generic extractor.
 */

import {
  type ExtractedAsset,
  extractGenericAssets,
  GENERIC_PHOTO_CAP,
} from "@repo/adapter-core";

const REA_CDN_RE = /\/\/(?:i\d*\.au\.reastatic\.net|i\.realestate\.com\.au)\//i;
const REA_NON_LISTING_IMG_RE = /\/logo\.|-crop[,/]/i;
const GALLERY_JSON_RE =
  /"__typename":"(MediaImage|MediaFloorplan)","id":"[0-9a-f]+","templatedUrl":"([^"]+\{size\}[^"]+)"/g;
const PHOTO_SIZE = "800x600";
const FLOORPLAN_SIZE = "1440x1080";
const PHOTO_EXTENSION_RE = /\.(png|jpg|jpeg|webp|avif)(?:\?|$|#)/i;
const FLOORPLAN_TESTID_RE = /data-testid=["'][^"']*floorplan[^"']*["']/i;
const FLOORPLAN_ALT_RE = /alt=["'][^"']*floor\s*-?\s*plan[^"']*["']/i;
const FLOORPLAN_CAP = 1;

/**
 * Strip `?fit=` and other cache-busting query params so the same photo
 * resolves to a single canonical source URL across re-scrapes. REA's
 * thumbnails differ only in `?fit=cover&w=640&h=480` etc.
 */
function canonicaliseReaUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.search = "";
    return parsed.toString();
  } catch {
    return url;
  }
}

function tryResolve(rel: string, baseUrl: string): string | null {
  try {
    return new URL(rel, baseUrl).toString();
  } catch {
    return null;
  }
}

/** Undo the JSON-in-a-string escaping (`\\"`, `\u002F`) of the embedded state. */
function unescapeEmbeddedJson(html: string): string {
  return html.replace(/\\u002F/gi, "/").replace(/\\+"/g, '"');
}

function extractReaGallery(html: string): ExtractedAsset[] {
  const photos: ExtractedAsset[] = [];
  const floorplans: ExtractedAsset[] = [];
  const seen = new Set<string>();
  for (const [, typename, templatedUrl] of unescapeEmbeddedJson(html).matchAll(GALLERY_JSON_RE)) {
    const isFloorplan = typename === "MediaFloorplan";
    const sourceUrl = templatedUrl.replace("{size}", isFloorplan ? FLOORPLAN_SIZE : PHOTO_SIZE);
    if (seen.has(sourceUrl)) continue;
    seen.add(sourceUrl);
    if (isFloorplan) {
      if (floorplans.length < FLOORPLAN_CAP) floorplans.push({ type: "floorplan", sourceUrl });
    } else if (photos.length < GENERIC_PHOTO_CAP) {
      photos.push({ type: "photo", sourceUrl, indexInListing: photos.length });
    }
  }
  return [...photos, ...floorplans];
}

function extractReaPhotos(html: string, baseUrl: string): ExtractedAsset[] {
  const assets: ExtractedAsset[] = [];
  const seen = new Set<string>();
  const re = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((match = re.exec(html)) !== null) {
    const tag = match[0];
    const rawSrc = match[1];
    if (!REA_CDN_RE.test(rawSrc)) continue;
    if (!PHOTO_EXTENSION_RE.test(rawSrc)) continue;
    // Agency logos and agent headshots share the CDN host.
    if (REA_NON_LISTING_IMG_RE.test(rawSrc)) continue;
    // Skip floorplan thumbnails — handled separately below.
    if (FLOORPLAN_TESTID_RE.test(tag) || FLOORPLAN_ALT_RE.test(tag)) continue;
    const resolved = tryResolve(rawSrc, baseUrl);
    if (!resolved) continue;
    const canonical = canonicaliseReaUrl(resolved);
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    assets.push({
      type: "photo",
      sourceUrl: canonical,
      indexInListing: assets.length,
    });
    if (assets.length >= GENERIC_PHOTO_CAP) break;
  }
  return assets;
}

function extractReaFloorplans(html: string, baseUrl: string): ExtractedAsset[] {
  const assets: ExtractedAsset[] = [];
  const seen = new Set<string>();
  const re = /<img[^>]+>/gi;
  let match: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((match = re.exec(html)) !== null) {
    const tag = match[0];
    if (!FLOORPLAN_TESTID_RE.test(tag) && !FLOORPLAN_ALT_RE.test(tag)) continue;
    const srcMatch = tag.match(/src=["']([^"']+)["']/i);
    if (!srcMatch) continue;
    const rawSrc = srcMatch[1];
    const resolved = tryResolve(rawSrc, baseUrl);
    if (!resolved) continue;
    const canonical = canonicaliseReaUrl(resolved);
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    assets.push({ type: "floorplan", sourceUrl: canonical });
    if (assets.length >= FLOORPLAN_CAP) break;
  }
  return assets;
}

function extractReaPdfs(html: string, baseUrl: string): ExtractedAsset[] {
  const assets: ExtractedAsset[] = [];
  const seen = new Set<string>();
  const re = /<a[^>]+href=["']([^"']+\.pdf(?:\?[^"']*)?)["'][^>]*>/gi;
  let match: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex-loop idiom
  while ((match = re.exec(html)) !== null) {
    const resolved = tryResolve(match[1], baseUrl);
    if (!resolved) continue;
    if (seen.has(resolved)) continue;
    seen.add(resolved);
    assets.push({ type: "pdf", sourceUrl: resolved });
  }
  return assets;
}

export function extractReaAssets(html: string, baseUrl: string): ExtractedAsset[] {
  const pdfs = extractReaPdfs(html, baseUrl);
  const gallery = extractReaGallery(html);
  if (gallery.length > 0) return [...gallery, ...pdfs];

  const photos = extractReaPhotos(html, baseUrl);
  const floorplans = extractReaFloorplans(html, baseUrl);

  // If we didn't find anything REA-specific, fall back to the generic
  // extractor so very old listing layouts (no testid, no CDN host) still
  // surface OG / JSON-LD images. The generic extractor will return at
  // most 10 photos itself.
  if (photos.length === 0 && floorplans.length === 0 && pdfs.length === 0) {
    return extractGenericAssets(html, baseUrl);
  }

  return [...photos, ...floorplans, ...pdfs];
}
